/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useSelector } from "react-redux";
import { API, CONFIGUREROUTES } from "../../globals/globals";
import { useGetAllNotesQuery, useGetDeviceCountsQuery, useGetDasRadioNodesQuery, useGetDasInventoryQuery, useGetDasFeatureFlagsQuery } from "../../store/api/devices/deviceApiSlice";
import { selectedDeviceSelector } from "../../store/api/devices/selectors";
import { useEffect } from "react";
import { setConfigCounts } from "../../store/api/deviceConfig/reducers";
import { useStoreDispatch } from "../../store/hooks";
import { useLocation } from "react-router-dom";
import { setDeviceDataCount } from "../../store/api/devices/reducers";
import { setIsLoadingStatus } from "../../store/helpers/helperReducers";
import { isDasModel } from "../Configuration/helpers";

export const useDeviceDrawerDetails = () => {
  const location = useLocation();
  const dispatch = useStoreDispatch();
  const locationPath = location?.pathname.split("/");
  const selectedDevice = useSelector(selectedDeviceSelector);
  const args = {
    filters: ``,
    deviceKey: selectedDevice?.objectKey || locationPath[2],
  };

  const { data, isLoading, refetch } = useGetDeviceCountsQuery(args, {
    pollingInterval: API.ENDPOINTS.DEVICECOUNTS.pollingInterval || API.POLLING_INTERVAL,
  });
  
  const deviceObjectKey = selectedDevice?.objectKey || locationPath[2];
  const { data: notesData = [], isLoading: isNotesLoading, isError: isNotesError, refetch: refetchNotes } = useGetAllNotesQuery({ deviceKey: deviceObjectKey });

  const isDas = isDasModel(selectedDevice?.modelName || "");

  const { data: featureFlags } = useGetDasFeatureFlagsQuery(selectedDevice?.modelName || "", {
    skip: !isDas || !selectedDevice?.modelName,
  });
  const inventoryDiscoveryEnabled = isDas ? (featureFlags?.inventoryDiscovery ?? true) : true;

  const { data: dasRadioNodesData } = useGetDasRadioNodesQuery(
    { deviceKey: deviceObjectKey, filters: "" },
    { skip: !isDas || !deviceObjectKey || !inventoryDiscoveryEnabled }
  );

  const { data: dasInventoryData } = useGetDasInventoryQuery(
    { deviceKey: deviceObjectKey, filters: "" },
    { skip: !isDas || !deviceObjectKey || !inventoryDiscoveryEnabled }
  );

  const notesCount = notesData.length;

  useEffect(() => {
    dispatch(setIsLoadingStatus({ deviceCounts: isLoading }));
  }, [dispatch, isLoading]);

  useEffect(() => {
    if (!!data) {
      dispatch(setDeviceDataCount(data));
      dispatch(
        setConfigCounts({
          [CONFIGUREROUTES.RN620.Path]: data?.rn620_Count || 0,
          [CONFIGUREROUTES.INVENTORY.Path]: data?.ru_Enabled + data?.ru_Disabled || 0,
          [CONFIGUREROUTES.RFFEED.Path]: data?.rffeed_Count || 0,
          [CONFIGUREROUTES.RFFEEDLATEST.Path]: data?.rffeed_Count || 0,
          [CONFIGUREROUTES.IPINTERFACE.Path]: data?.lan_Up + data?.lan_Down || 0,
          [CONFIGUREROUTES.RADIONODE.Path]: data?.rn_Up + data?.rn_Down || 0,
          [CONFIGUREROUTES.NRCELL.Path]: data?.nr_Enabled + data?.nr_Disabled || 0,
          [CONFIGUREROUTES.LTECELL.Path]: data?.lte_Up + data?.lte_Down || 0,
          [CONFIGUREROUTES.NGX2CONNECTION.Path]: data?.ng_X2_Connected + data?.ng_X2_Disconnected || 0,
          [CONFIGUREROUTES.LTEX2CONNECTION.Path]: data?.lte_X2_Connected + data?.lte_X2_Disconnected || 0,
          [CONFIGUREROUTES.DU.Path]: data?.du_Up + data?.du_Down || 0,
          [CONFIGUREROUTES.ZONES.Path]: selectedDevice?.nodeType === "SN" ? data?.lte_Zone_Count : data?.ng_Zone_Count || 0,
        })
      );
    }
  }, [data, dispatch, selectedDevice?.nodeType]);
  
  useEffect(() => {
    if (isDas) {
      dispatch(
        setConfigCounts({
          [CONFIGUREROUTES.DASRADIONODES.Path]: dasRadioNodesData?.length || 0,
          [CONFIGUREROUTES.DASINVENTORY.Path]: dasInventoryData?.length || 0,
        })
      );
    }
  }, [isDas, dasRadioNodesData, dasInventoryData, dispatch]);

  return { data, isLoading, refetch, notesCount };
};
